import type { Message, MessageSource } from '../types/messages';
import { createMessage, updateMessageContent, createLoadingIndicator, createFeedbackButtons, createSourcesStrip } from './Message';

/**
 * Scrollable message list component.
 */

const DEFAULT_GREETING_TITLE = 'Ask anything about the docs.';
const DEFAULT_GREETING_SUBTITLE = 'Type your question below.';

export interface MessageListProps {
  messages: Message[];
  avatarUrl?: string;
  onCopyCode?: (code: string) => void;
  onFeedback?: (messageId: string, rating: 'up' | 'down') => void;
  greetingTitle?: string;
  greetingSubtitle?: string;
}

export interface MessageListElements {
  container: HTMLDivElement;
  messagesMap: Map<string, HTMLDivElement>;
  loadingIndicator: HTMLDivElement | null;
  greeting: HTMLDivElement | null;
}

export function createMessageList(props: MessageListProps): MessageListElements {
  const container = document.createElement('div');
  container.className = 'messages-container';
  // role=log + aria-live so screen readers announce streamed replies as they arrive.
  container.setAttribute('role', 'log');
  container.setAttribute('aria-live', 'polite');
  container.setAttribute('aria-relevant', 'additions');

  const messagesMap = new Map<string, HTMLDivElement>();

  // Greeting hero sits first so the empty state reads as a prompt, not a void.
  const greeting = shouldRenderGreeting(props) ? createGreeting(props) : null;
  if (greeting) {
    container.appendChild(greeting);
  }

  // The initial batch cascades in via a per-index animation-delay; live
  // messages arrive one at a time (addMessage) and stay at delay 0.
  props.messages.forEach((message, index) => {
    const messageEl = createMessage({
      message,
      avatarUrl: props.avatarUrl,
      onCopyCode: props.onCopyCode,
      onFeedback: props.onFeedback,
    });
    if (index > 0) {
      messageEl.style.animationDelay = `${Math.min(index * 0.06, 0.3)}s`;
    }
    messagesMap.set(message.id, messageEl);
    container.appendChild(messageEl);
  });

  return { container, messagesMap, loadingIndicator: null, greeting };
}

/**
 * The greeting shows only when explicit greeting copy is configured.
 * Otherwise the initial_message bubble owns the empty state (rendering it
 * here too would show the same text twice).
 */
function shouldRenderGreeting(props: MessageListProps): boolean {
  return Boolean(props.greetingTitle || props.greetingSubtitle);
}

/**
 * Empty-state greeting hero.
 */
function createGreeting(props: MessageListProps): HTMLDivElement {
  const greeting = document.createElement('div');
  greeting.className = 'chat-greeting';

  const title = document.createElement('h2');
  title.className = 'chat-greeting-title';
  title.textContent = props.greetingTitle || DEFAULT_GREETING_TITLE;
  greeting.appendChild(title);

  const subtitle = document.createElement('p');
  subtitle.className = 'chat-greeting-subtitle';
  subtitle.textContent = props.greetingSubtitle || DEFAULT_GREETING_SUBTITLE;
  greeting.appendChild(subtitle);

  return greeting;
}

/**
 * Add a new message to the list.
 */
export function addMessage(
  elements: MessageListElements,
  message: Message,
  avatarUrl?: string,
  onCopyCode?: (code: string) => void,
  onFeedback?: (messageId: string, rating: 'up' | 'down') => void
): void {
  // Remove loading indicator if present
  if (elements.loadingIndicator) {
    elements.loadingIndicator.remove();
    elements.loadingIndicator = null;
  }

  // First user turn dismisses the empty-state greeting.
  if (message.role === 'user' && elements.greeting) {
    elements.greeting.remove();
    elements.greeting = null;
  }

  const messageEl = createMessage({
    message,
    avatarUrl,
    onCopyCode,
    onFeedback,
  });

  elements.messagesMap.set(message.id, messageEl);
  elements.container.appendChild(messageEl);
  scrollToBottom(elements.container);
}

/**
 * Update an existing message (for streaming).
 * Returns Promise that resolves when async rendering is complete.
 */
export function updateMessage(
  elements: MessageListElements,
  messageId: string,
  content: string,
  isStreaming: boolean,
  onCopyCode?: (code: string) => void,
  onFeedback?: (messageId: string, rating: 'up' | 'down') => void,
  sources?: MessageSource[]
): Promise<void> {
  const messageEl = elements.messagesMap.get(messageId);
  if (!messageEl) return Promise.resolve();

  return updateMessageContent(messageEl, content, isStreaming, onCopyCode).then(() => {
    // Sources and feedback are added when streaming ends (createMessage only
    // renders them for messages that already carry them at mount time).
    // Stack order matches createMessage: bubble, sources, feedback.
    if (!isStreaming) {
      const stack = messageEl.querySelector('.message-stack');
      if (stack) {
        if (sources?.length && !messageEl.querySelector('.message-sources')) {
          stack.appendChild(createSourcesStrip(sources));
        }
        if (!messageEl.querySelector('.message-feedback')) {
          const feedbackEl = createFeedbackButtons(messageId, onFeedback);
          stack.appendChild(feedbackEl);
        }
      }
    }
    scrollToBottom(elements.container);
  });
}

/**
 * Show loading indicator.
 */
export function showLoading(elements: MessageListElements): void {
  if (elements.loadingIndicator) return;

  elements.loadingIndicator = createLoadingIndicator();
  elements.container.appendChild(elements.loadingIndicator);
  scrollToBottom(elements.container);
}

/**
 * Hide loading indicator.
 */
export function hideLoading(elements: MessageListElements): void {
  if (elements.loadingIndicator) {
    elements.loadingIndicator.remove();
    elements.loadingIndicator = null;
  }
}

/**
 * Clear all messages.
 */
export function clearMessages(elements: MessageListElements): void {
  elements.container.innerHTML = '';
  elements.messagesMap.clear();
  elements.loadingIndicator = null;
  elements.greeting = null;
}

/**
 * Scroll to the bottom of the messages.
 */
function scrollToBottom(container: HTMLElement): void {
  // Use requestAnimationFrame for smooth scrolling after DOM update
  requestAnimationFrame(() => {
    container.scrollTop = container.scrollHeight;
  });
}
